import { Button, chipSetService, soundManager, SoundType, useChipSetStore, Image, t } from '@slot-nx/core'
import { Container, Text } from '@pixi/react'
import { TextStyle } from 'pixi.js'
import { useEffect, useState, useMemo } from 'react'
import { changeFontSize, formatNumberWithComma } from '../domain/game/util/gameUtil'
import { BetOptionType, getPayStake } from '../domain/placeOrder/utils/stakeUtils'
import { useGameStore } from '../domain/game/store/GameStore'

const textStakeStyle = new TextStyle({ fontSize: 40, fill: '#FFFFFF', align: 'center', fontWeight: 'bold' })
const textSmallStyle = new TextStyle({ fill: '#ffffff', align: 'center', fontWeight: 'bold' })

/** 420×520 卡片資產上空白 CTA 區域的中心（與 Button 元件本地座標一致，sprite 左上角為原點） */
const BUY_BONUS_CARD_LABEL_X = 210
const BUY_BONUS_CARD_LABEL_Y = 472

const buyBonusCardLabelStyle = new TextStyle({
  align: 'center',
  fontFamily: 'RobotoCondensed',
  fontWeight: 'bold',
  fontSize: 44,
  fill: '#F3E5A1',
  stroke: '#2a1a0a',
  strokeThickness: 4,
  wordWrap: true,
  wordWrapWidth: 380,
  lineHeight: 50,
})

interface SelectContentProps {
  setShowConfirmContent: React.Dispatch<React.SetStateAction<boolean>>
}

export const SelectContent = (props: SelectContentProps) => {
  const { setShowConfirmContent } = props
  const [currentIndex, chipSet] = useChipSetStore(x => [x.currentIndex, x.chipSet])
  const [tempIndex, setTempIndex] = useState(currentIndex)

  useEffect(() => {
    setTempIndex(currentIndex)
  }, [currentIndex])

  const leftBetInfo = useMemo(() => {
    if (!chipSet[tempIndex]) return { text: '', fontSize: 40 }
    const pay = getPayStake(chipSet[tempIndex], BetOptionType.FreeGame)
    const formattedPay = formatNumberWithComma(pay)
    return {
      text: formattedPay,
      fontSize: changeFontSize(formattedPay),
    }
  }, [tempIndex, chipSet])

  const rightBetInfo = useMemo(() => {
    if (!chipSet[tempIndex]) return { text: '', fontSize: 40 }
    const pay = getPayStake(chipSet[tempIndex], BetOptionType.ThreeCombo)
    const formattedPay = formatNumberWithComma(pay)
    return {
      text: formattedPay,
      fontSize: changeFontSize(formattedPay),
    }
  }, [tempIndex, chipSet])

  return (
    <Container name="SelectContent">
      <Image name="frameBet" image="image_betFrame" x={0} y={260} anchor={0.5} />
      <Button
        name="minus button"
        enabled={tempIndex > 0}
        x={-258}
        y={224}
        width={72}
        height={72}
        defaultView="minus_normal"
        hoverView="minus_hover"
        disabledView="minus_disable"
        pressedView="minus_click"
        onPressing={() => soundManager.play(SoundType.stake_minus)}
        onPress={() => chipSetService.selectPreviousChip()}
      />
      <Button
        name="plus button"
        enabled={tempIndex < chipSet.length - 1}
        x={185}
        y={224}
        width={72}
        height={72}
        defaultView="plus_normal"
        hoverView="plus_hover"
        disabledView="plus_disable"
        pressedView="plus_click"
        onPressing={() => soundManager.play(SoundType.stake_plus)}
        onPress={() => chipSetService.selectNextChip()}
      />
      <Container name="buyFreeGame_card" x={-440} y={-320}>
        <Button
          name="buyFreeGame"
          x={0}
          y={0}
          enabled
          visible
          width={420}
          height={520}
          defaultView="btn_buyFreeGame_normal"
          hoverView="btn_buyFreeGame_hover"
          disabledView="btn_buyFreeGame_disable"
          pressedView="btn_buyFreeGame_click"
          onPressing={() => soundManager.play(SoundType.buttonConfirm)}
          onPress={() => {
            useGameStore.setState({ betOptionType: BetOptionType.FreeGame })
            useChipSetStore.setState({ tempIndex })
            setShowConfirmContent(true)
          }}
        />
        <Text
          name="buyFreeGame_buy_label"
          text={t('buyBonus_popup_buy_button')}
          x={BUY_BONUS_CARD_LABEL_X}
          y={BUY_BONUS_CARD_LABEL_Y}
          anchor={[0.5, 0.5]}
          style={buyBonusCardLabelStyle}
          eventMode="none"
        />
      </Container>
      <Container name="buy3Combo_card" x={0} y={-320}>
        <Button
          name="buyGoldFreeGame"
          x={0}
          y={0}
          enabled
          visible
          width={420}
          height={520}
          defaultView="btn_buy3Combo_normal"
          hoverView="btn_buy3Combo_hover"
          disabledView="btn_buy3Combo_disable"
          pressedView="btn_buy3Combo_click"
          onPressing={() => soundManager.play(SoundType.buttonConfirm)}
          onPress={() => {
            useGameStore.setState({ betOptionType: BetOptionType.ThreeCombo })
            useChipSetStore.setState({ tempIndex })
            setShowConfirmContent(true)
          }}
        />
        <Text
          name="buy3Combo_buy_label"
          text={t('buyBonus_popup_buy_button')}
          x={BUY_BONUS_CARD_LABEL_X}
          y={BUY_BONUS_CARD_LABEL_Y}
          anchor={[0.5, 0.5]}
          style={buyBonusCardLabelStyle}
          eventMode="none"
        />
      </Container>

      <Container name="left_bet_container" width={306} height={40} x={-235} y={70}>
        <Text
          name="left_bet"
          anchor={[0.5, 0.5]}
          text={leftBetInfo.text}
          style={new TextStyle({ ...textSmallStyle, fontSize: leftBetInfo.fontSize })}
        />
      </Container>
      <Container name="right_bet_container" width={306} height={40} x={210} y={70}>
        <Text
          name="right_bet"
          anchor={[0.5, 0.5]}
          text={rightBetInfo.text}
          style={new TextStyle({ ...textSmallStyle, fontSize: rightBetInfo.fontSize })}
        />
      </Container>

      <Text
        name="stake"
        text={chipSet[tempIndex] ? formatNumberWithComma(chipSet[tempIndex]) : ''}
        x={0}
        y={240}
        anchor={[0.5, 0]}
        style={textStakeStyle}
      />
    </Container>
  )
}
